import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import {
  Body2,
  Button,
  Checkbox,
  ConfirmDialog,
  Header2,
  InputUrl,
  Label,
  LayerContextProvider,
  layers,
} from "@activecollab/components";
import { Field, Form, Formik } from "formik";
import { List } from "immutable";
import isEqual from "lodash/isEqual";
import moment from "moment";
import PropTypes from "prop-types";
import * as yup from "yup";
import { SubscribersAvatarList } from "~/components/SubscribersAvatarList/SubscribersAvatarList";
import { SubscribersPicker } from "~/components/tasks/TaskPanel/subscribers/SubscribersPicker";
import { getConfigOption } from "~/store/selectors/selector.configOptions";
import { canAccessWorkload } from "~/store/selectors/selector.users";
import { keyCodes } from "~/utils/key_codes";
import { CalendarEventParticipants } from "./CalendarEventParticipants";
import { CalendarEventRepeatProperty } from "./CalendarEventRepeatProperty";
import { CalendarEventSelectDate } from "./CalendarEventSelectDate";
import { CalendarSelect } from "./CalendarSelect";
import AngDropFiles from "../component.ang_drop_files";
import { CustomFormField } from "../CustomFormField";
import { FormikEffect } from "../FormikEffect";
import { useFileHandler } from "../Hooks";
import { AttachmentUpload } from "../select_attachments/AttachmentUpload";
import { SelectAttachments } from "../select_attachments/SelectAttachments";
import VisualEditorContainer from "../VisualEditor/VisualEditorContainer";

const normalizeData = (attributes) => {
  const { attachments, ...rest } = attributes;

  return {
    attach_uploaded_files: attachments.filesToAttach,
    drop_attached_files: attachments.filesToDrop,
    ...rest,
  };
};

const CalendarEventForm = ({
  name,
  calendarEventId,
  description,
  subscribers,
  meetingUrl,
  calendarId,
  endsOn,
  startsOn,
  startsOnTime,
  endsOnTime,
  repeatUntil,
  repeatEvent,
  recurrence_instance,
  onSubmit,
  onCancel,
  attachments,
  attachUploadedFiles,
  dropAttachedFiles,
  submitButtonLabel,
  userIds,
  participants,
  visibleOnWorkload,
  hasDraft,
  onChange,
}) => {
  const { t } = useTranslation();
  const submitButtonRef = useRef(null);
  const inputRef = useRef(null);
  const formikRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const userCanAccessWorkload = useSelector(canAccessWorkload);
  const workloadEnabled = useSelector((state) => getConfigOption(state, `workload_enabled`));

  const validationSchema = yup.object().shape({
    starts_on: yup.date().required(),
    ends_on: yup.date().required(),
    starts_on_time: yup.string().nullable(),
    ends_on_time: yup.string().nullable(),
    name: yup.string().required(),
    calendar_id: yup.number().required(),
    meeting_url: yup.lazy((value) => (["http://", "https://"].includes(value) ? yup.string() : yup.string().url())),
    participants: yup.array().min(1).required(),
    subscribers: yup.array().of(yup.number()),
  });

  const handleSubmit = useCallback(
    (values) => {
      const { body, name, subscribers, attachments, calendar_id, meeting_url, participants } = values;

      const attributes = {
        body: body,
        name,
        reset_attachments: true,
        reset_subscribers: true,
        starts_on: values.starts_on,
        starts_on_time: values.starts_on_time,
        ends_on_time: values.ends_on_time,
        ends_on: values.ends_on,
        subscribers,
        repeat_event: values.repeat_event,
        repeat_until: values.repeat_until,
        calendar_id: calendar_id,
        meeting_url: ["http://", "https://"].includes(meeting_url) ? null : meeting_url,
        participants,
        is_visible_on_workload: values.is_visible_on_workload,
      };

      if (attachments.filesToAttach) {
        attributes.attach_uploaded_files = attachments.filesToAttach;
      }

      if (attachments.filesToDrop) {
        attributes.drop_attached_files = attachments.filesToDrop;
      }

      if (typeof onSubmit === "function") {
        onSubmit(attributes).then((action) => {
          if (!action?.error) {
            formikRef.current.setSubmitting(false);
          }
        });
      }
    },
    [onSubmit]
  );
  const handleKeyPress = useCallback(
    (event) => {
      if (submitButtonRef.current && event.key === keyCodes.enter && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        submitButtonRef.current.click();
      }
      if (event.key === keyCodes.escape) {
        event.preventDefault();
        if (hasDraft) {
          setShowConfirmationModal(true);
        } else if (typeof onCancel === "function") {
          onCancel();
        }
      }
    },
    [hasDraft, onCancel]
  );

  const { uploading, onUpload, ...fileHandlerRest } = useFileHandler();

  const updateParticipants = useCallback(async (emails) => {
    await formikRef.current.setFieldValue("participants", emails);
  }, []);

  const handleCancel = useCallback(() => {
    if (hasDraft) {
      setShowConfirmationModal(true);
    } else if (typeof onCancel === "function") {
      onCancel();
    }
  }, [hasDraft, onCancel]);

  const handleOpenFileWindow = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleConfirmModalSubmit = useCallback(() => {
    if (typeof onCancel === "function") {
      onCancel();
    }
    setShowConfirmationModal(false);
  }, [onCancel]);

  const handleCloseConfirmationModal = useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  const handleUpload = useCallback(
    (files) => {
      if (typeof onUpload === "function") {
        for (let i = 0; i < files.length; i++) {
          onUpload(files[i]);
        }
      }
    },
    [onUpload]
  );

  const handleKeyPressEditor = useCallback((event) => {
    if (event.key === keyCodes.enter && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
    }
  }, []);

  const handleChangeDates = useCallback(async (_startsOn, _endsOn, _startsOnTime, _endsOnTime) => {
    await formikRef.current.setFieldValue("starts_on", _startsOn);
    await formikRef.current.setFieldValue("ends_on", _endsOn);
    await formikRef.current.setFieldValue("starts_on_time", _startsOnTime);
    await formikRef.current.setFieldValue("ends_on_time", _endsOnTime);
    return new Promise((resolve) => resolve(true));
  }, []);

  const onRepeatEventChange = useCallback(async (data) => {
    await formikRef.current.setFieldValue(Object.keys(data)[0], Object.values(data)[0]);
  }, []);

  useEffect(() => {
    inputRef?.current?.focus();
  }, []);

  useUpdateEffect(() => {
    if (subscribers && !isEqual(formikRef.current?.values.subscribers, subscribers)) {
      formikRef.current.setFieldValue("subscribers", subscribers);
    }
  }, [subscribers]);

  const handleCalendarChange = useCallback(
    async (calendarId, attributes) => {
      await formikRef.current.setFieldValue("calendar_id", calendarId);
      if (attributes && workloadEnabled) {
        await formikRef.current.setFieldValue("is_visible_on_workload", attributes?.is_visible_on_workload);
      } else {
        await formikRef.current.setFieldValue("is_visible_on_workload", false);
      }
    },
    [workloadEnabled]
  );

  const handleChange = useCallback(
    (_, attributes) => {
      onChange(normalizeData(attributes));
    },
    [onChange]
  );

  return (
    <Formik
      initialValues={{
        name,
        body: description ? description : "",
        subscribers: subscribers,
        starts_on_time: startsOnTime,
        starts_on: startsOn,
        ends_on_time: endsOnTime,
        ends_on: endsOn,
        repeat_event: repeatEvent,
        repeat_until: repeatUntil,
        recurrence_instance: recurrence_instance,
        participants: participants,
        attachments: {
          filesToAttach: attachUploadedFiles,
          filesToDrop: dropAttachedFiles,
        },
        meeting_url: meetingUrl,
        calendar_id: calendarId,
        is_visible_on_workload: workloadEnabled ? visibleOnWorkload : false,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      innerRef={formikRef}
    >
      {({ isSubmitting, isValid, values, setFieldValue }) => (
        <Form
          onKeyDown={handleKeyPress}
          data-testid="calendar-event-form"
          className="tw-w-full"
        >
          <div>
            <div
              className="header tw-bg-primary-300 tw-items-center tw-flex tw-flex-col tw-px-8"
              data-testid="calendar-header"
            >
              <div className="tw-w-full tw-flex tw-justify-center tw-items-center">
                <div className="tw-flex tw-flex-col  tw-items-center tw-w-full">
                  <div className="tw-flex tw-items-center tw-justify-center tw-mt-[20px] tw-w-full tw-px-12">
                    <Header2 className=" tw-leading-[24px] tw-truncate tw-max-w-full">{t("Schedule Event")}</Header2>
                  </div>
                  <div className="tw-border-t tw-border-solid tw-border-0 tw-border-border-primary tw-w-full tw-my-3" />
                </div>
              </div>
              <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-justify-center tw-w-full tw-gap-10 tw-pb-[20px]">
                <CalendarEventSelectDate
                  startsOn={
                    values.starts_on_time
                      ? moment(`${values.starts_on} ${values.starts_on_time}`).unix()
                      : moment.utc(`${values.starts_on} 00:00:00`).unix()
                  }
                  endsOn={
                    values.ends_on_time
                      ? moment(`${values.ends_on} ${values.ends_on_time}`).unix()
                      : moment.utc(`${values.ends_on} 00:00:00`).unix()
                  }
                  startsOnTime={values.starts_on_time}
                  endsOnTime={values.ends_on_time}
                  onSave={handleChangeDates}
                  canEdit
                />
                <CalendarEventRepeatProperty
                  repeatEvent={values.repeat_event}
                  repeatUntil={moment.utc(values.repeat_until).unix()}
                  canEdit
                  disableDaysBefore={moment.utc(`${values.starts_on} 00:00:00`).unix()}
                  onChange={onRepeatEventChange}
                />
              </div>
            </div>
            <FormikEffect onChange={handleChange} />

            <>
              <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-2 tw-max-h-full tw-flex-1 sm:tw-pr-3 lg:tw-pr-0 tw-overflow-y-auto tw-p-10">
                <div className="tw-flex-1 tw-rounded-md tw-max-w-[660px] tw-bg-page-paper-main  lg:tw-overflow-y-auto tw-flex tw-flex-col tw-max-h-full">
                  <div className="tw-flex tw-flex-col tw-h-full lg:tw-overflow-y-auto">
                    <Field name="name">
                      {({ field }) => (
                        <input
                          ref={inputRef}
                          id="eventNameInput"
                          type="text"
                          tabIndex={0}
                          maxLength={150}
                          placeholder={`${t("Event name")} *`}
                          data-testid="calendar-name-input"
                          className="tw-text-lg tw-font-bold tw-px-6 tw-py-4 tw-outline-none tw-w-full tw-bg-transparent tw-text-theme-900 tw-border-border-primary tw-border-solid tw-border tw-rounded-t-md tw-border-b-0"
                          {...field}
                        />
                      )}
                    </Field>
                    <div className="tw-border-border-primary tw-border-solid tw-border tw-flex-1 lg:tw-overflow-y-auto tw-border-t-transparent task_form_description_container">
                      <CustomFormField name="body">
                        {({ field }) => (
                          <VisualEditorContainer
                            parentType="CalendarEvent"
                            parentId={calendarEventId}
                            content={field.value}
                            onKeyDown={handleKeyPressEditor}
                            onChange={field.onChange}
                            placeholder={t(`Write a description or press "/" for commands`)}
                          />
                        )}
                      </CustomFormField>
                    </div>
                    <div className="tw-px-6 tw-py-5 tw-border tw-border-border-primary tw-border-solid tw-overflow-hidden tw-border-t-transparent">
                      <CustomFormField name="attachments">
                        {({ field }) => (
                          <SelectAttachments
                            {...fileHandlerRest}
                            uploading={uploading}
                            attachmentIds={List(attachments)}
                            filesToAttach={field.value.filesToAttach}
                            filesToDrop={field.value.filesToDrop}
                            onChange={field.onChange}
                            onOpenFileWindow={handleOpenFileWindow}
                          />
                        )}
                      </CustomFormField>
                    </div>
                    <div className="tw-px-6 tw-h-[66px] tw-flex tw-items-center tw-border tw-border-border-primary tw-border-solid tw-overflow-hidden tw-border-t-transparent tw-rounded-b-md">
                      <Body2
                        weight="bold"
                        className="tw-truncate tw-w-[100px] tw-mr-1"
                      >
                        {t("Subscribers")}
                      </Body2>
                      <CustomFormField name="subscribers">
                        {({ field }) => {
                          return (
                            <SubscribersPicker
                              onChange={field.onChange}
                              hideClients={false}
                              selected={field.value}
                              userIds={userIds}
                              target={
                                <SubscribersAvatarList
                                  subscriberIds={field.value}
                                  limit={5}
                                />
                              }
                            />
                          );
                        }}
                      </CustomFormField>
                    </div>
                  </div>
                  <AngDropFiles onDrop={handleUpload} />
                </div>
                <div className="tw-flex tw-flex-col tw-gap-6 tw-w-[328px] lg:tw-px-6 tw-mt-6 lg:tw-mt-0">
                  <div className="tw-h-[52px] tw-flex tw-flex-col tw-justify-between">
                    <Label
                      size="small"
                      weight="bold"
                      className="tw-mb-1 tw-block"
                    >
                      {t("Calendar")}
                    </Label>
                    <CustomFormField name="calendar_id">
                      {({ field }) => (
                        <CalendarSelect
                          selected={field.value}
                          onChange={handleCalendarChange}
                          disabled={recurrence_instance !== null}
                        />
                      )}
                    </CustomFormField>
                  </div>
                  <div className="tw-flex tw-justify-between tw-items-center">
                    <Label
                      size="small"
                      weight="bold"
                      className="tw-mb-1 tw-block"
                    >
                      {`${t("Participants")} *`}
                    </Label>
                    <CustomFormField name="participants">
                      {({ field }) => (
                        <CalendarEventParticipants
                          calendarId={values.calendar_id}
                          participants={field.value}
                          onClose={updateParticipants}
                        />
                      )}
                    </CustomFormField>
                  </div>
                  {userCanAccessWorkload ? (
                    <div className="tw-flex tw-gap-[6px] tw-items-center tw-min-h-5">
                      <Field name="is_visible_on_workload">
                        {({ field }) => (
                          <Checkbox
                            name="is_visible_on_workload"
                            id="is_visible_on_workload"
                            checked={Boolean(field.value)}
                            onChange={(event) => {
                              setFieldValue("is_visible_on_workload", event.target.checked);
                            }}
                          />
                        )}
                      </Field>
                      <Label
                        htmlFor="is_visible_on_workload"
                        className="tw-cursor-pointer"
                        weight="regular"
                      >
                        {t("Add to participants' workload")}
                      </Label>
                    </div>
                  ) : null}
                  <div>
                    <Label
                      size="small"
                      weight="bold"
                      className="tw-mb-1 tw-block"
                    >
                      {t("Meeting Link")}
                    </Label>
                    <CustomFormField name="meeting_url">
                      {({ field }) => (
                        <InputUrl
                          className="tw-w-auto"
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    </CustomFormField>
                  </div>
                </div>
              </div>
              <div className="tw-pt-4 tw-pb-8 tw-px-10">
                <Button
                  ref={submitButtonRef}
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting || !isValid || uploading}
                  tabIndex={0}
                  className="tw-mr-3"
                  data-qa-id="add-form-button"
                >
                  {uploading ? `${t("Uploading")}...` : submitButtonLabel}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancel}
                  tabIndex={0}
                  data-qa-id="cancel-task-button"
                >
                  {t("Cancel")}
                </Button>
              </div>
            </>
          </div>
          <LayerContextProvider value={{ zIndex: layers.skyscraper }}>
            <ConfirmDialog
              open={showConfirmationModal}
              dialogTitle={t("Discard changes?")}
              dialogContent={t("All unsaved changes will be lost.")}
              confirmBtnText={t("Discard")}
              cancelBtnText={t("Cancel")}
              onConfirm={handleConfirmModalSubmit}
              onCancel={handleCloseConfirmationModal}
            />
          </LayerContextProvider>
          <AttachmentUpload
            ref={fileInputRef}
            multiple
            onUpload={onUpload}
          />
        </Form>
      )}
    </Formik>
  );
};

CalendarEventForm.propTypes = {
  calendarEventId: PropTypes.number,
  name: PropTypes.string,
  meetingUrl: PropTypes.string,
  calendarId: PropTypes.number,
  endsOn: PropTypes.string,
  startsOn: PropTypes.string,
  startsOnTime: PropTypes.string,
  endsOnTime: PropTypes.string,
  repeatEvent: PropTypes.string,
  repeatUntil: PropTypes.string,
  recurrence_instance: PropTypes.number,
  description: PropTypes.string,
  attachments: PropTypes.array,
  subscribers: PropTypes.array,
  participants: PropTypes.array,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  onChange: PropTypes.func,
  pendingSubmit: PropTypes.bool,
  hasDraft: PropTypes.bool,
  attachUploadedFiles: PropTypes.arrayOf(PropTypes.string),
  dropAttachedFiles: PropTypes.arrayOf(PropTypes.number),
  submitButtonLabel: PropTypes.string,
  userIds: PropTypes.array,
  visibleOnWorkload: PropTypes.bool,
  enabledWorkload: PropTypes.bool,
  featureEventOnWorkload: PropTypes.bool,
};

CalendarEventForm.defaultProps = {
  calendarEventId: 0,
  name: ``,
  description: ``,
  attachments: [],
  subscribers: [],
  onCancel: () => null,
  onSubmit: () => null,
  onChange: () => null,
  attachUploadedFiles: [],
  dropAttachedFiles: [],
  participants: [],
  submitButtonLabel: "Add Event",
};

export default CalendarEventForm;
